import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createViewDealCreate } from "@/modules/lasalle/partners/views/ViewDealCreate";
import { routerPaths } from "@/modules/lasalle/router";
import { NOOP } from "@/utils/utils";
import type { TApiDeal, TApiDealFileType } from "@/apis/proda_finance";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import type { TAsyncState } from "@/store";
import { buildDealCopyPrefill } from "@/ui/organisms/DealWizard";
import type { TDealCopyPrefill } from "@/ui/organisms/DealWizard";
import { getNow } from "@/utils/dateService";
import { buildXlsxFile } from "@/utils/downloadXlsx";
import { fetchAllPages } from "./wireDealDetail";
import { PROPCODE_MAPPING_COLUMNS, propcodeMappingFileRow } from "./fileFormats";

/**
 * The prefill for copying a LaSalle deal. Its propcode file is rebuilt in the
 * columns of LaSalle's Property Code Mapping file, which the copy is validated
 * against. Its OTB is the source deal's Yardi Trial Balance, as uploaded.
 */
export function buildLaSalleDealCopyPrefill(
    source: Parameters<typeof buildDealCopyPrefill>[0],
    now: Date,
): TDealCopyPrefill {
    const prefill = buildDealCopyPrefill(source, now);
    return {
        ...prefill,
        propcodeFile:
            prefill.propcodeFile &&
            buildXlsxFile(
                prefill.propcodeFile.name,
                PROPCODE_MAPPING_COLUMNS,
                prefill.propcodeRows.map((row) =>
                    propcodeMappingFileRow(row.source_propcode, row.target_propcode),
                ),
                "Propcode Mappings",
            ),
    };
}

export function wireDeals({ useAppStore, api }: TCompositionDeps) {
    const ViewDealCreate = createViewDealCreate({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const navigate = useNavigate();

            const [preselectedServiceProviderId, setPreselectedServiceProviderId] = useState<
                string | null
            >(null);

            const [serviceProviders, setServiceProviders] = useState<{
                data: Awaited<ReturnType<typeof api.serviceProviders.list>>["items"] | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [portfolioCompanies, setPortfolioCompanies] = useState<{
                data: Array<{
                    id: string;
                    name: string;
                    code: string;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [users, setUsers] = useState<{
                data: Awaited<ReturnType<typeof api.users.list>>["items"] | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [copySource, setCopySource] = useState<TAsyncState<TDealCopyPrefill>>({
                status: "idle",
                data: null,
                error: null,
            });

            // Read everything the wizard needs from the deal being copied. Nothing is
            // written: the copy is only created when the wizard is submitted.
            const loadCopySource = useCallback(async (dealId: string) => {
                setCopySource({ status: "pending", data: null, error: null });
                try {
                    const [deal, columnMappings, propcodeMappings, otbInfo] = await Promise.all([
                        api.deals.get(dealId),
                        fetchAllPages((skip, limit) =>
                            api.deals.getColumnMappings(dealId, { skip, limit }),
                        ),
                        fetchAllPages((skip, limit) =>
                            api.deals.getPropcodeMappings(dealId, { skip, limit }),
                        ),
                        api.deals.getOTBInfo(dealId).catch(() => null),
                    ]);

                    let otbFile: File | null = null;
                    let otbUnavailableReason: string | null = null;
                    if (otbInfo?.has_otb && otbInfo.file_id) {
                        try {
                            otbFile = await api.files.fetchOriginal(otbInfo.file_id);
                        } catch {
                            otbUnavailableReason =
                                "The Yardi Trial Balance of the copied deal could not be loaded. Upload this deal's.";
                        }
                    } else if (otbInfo === null) {
                        otbUnavailableReason =
                            "The Yardi Trial Balance of the copied deal could not be loaded. Upload this deal's.";
                    }

                    setCopySource({
                        status: "fulfilled",
                        data: buildLaSalleDealCopyPrefill(
                            {
                                deal,
                                columnMappings,
                                propcodeMappings,
                                otbFile,
                                otbUnavailableReason,
                            },
                            getNow(),
                        ),
                        error: null,
                    });
                } catch (err) {
                    setCopySource({
                        status: "rejected",
                        data: null,
                        error:
                            err instanceof Error
                                ? err
                                : new Error("Failed to load the deal to copy"),
                    });
                }
            }, []);

            const loadAll = useCallback(
                async (bpId?: string) => {
                    // Store preselected service provider ID
                    if (bpId) {
                        setPreselectedServiceProviderId(bpId);
                    }

                    setServiceProviders({ data: null, loading: true });
                    try {
                        const spData = await api.serviceProviders.list({
                            skip: 0,
                            limit: 1000,
                            business_partner_type: "service_provider",
                        });
                        setServiceProviders({ data: spData.items, loading: false });
                    } catch {
                        setServiceProviders({ data: [], loading: false });
                    }

                    setPortfolioCompanies({ data: null, loading: true });
                    try {
                        const pcData = await api.portfolioCompanies.list({
                            skip: 0,
                            limit: 100,
                        });
                        setPortfolioCompanies({
                            data: pcData.items.map((pc) => ({
                                id: pc.id,
                                name: pc.name,
                                code: pc.code,
                            })),
                            loading: false,
                        });
                    } catch {
                        setPortfolioCompanies({ data: [], loading: false });
                    }

                    setUsers({ data: null, loading: true });
                    try {
                        const userData = await api.users.list({
                            page_size: 1000,
                            finance_team: true,
                        });
                        setUsers({ data: userData.items, loading: false });
                    } catch {
                        setUsers({ data: [], loading: false });
                    }
                },
                [effectiveCompanyId],
            );

            return {
                onMount: loadAll,
                onUnmount: NOOP,
                useServiceProviders: () => serviceProviders,
                usePortfolioCompanies: () => portfolioCompanies,
                useUsers: () => users,
                usePreselectedServiceProviderId: () => preselectedServiceProviderId,
                onCreateDeal: useCallback(async (data: Parameters<typeof api.deals.create>[0]) => {
                    const createdDeal = await api.deals.create(data);
                    return createdDeal;
                }, []),
                onCheckDealNameTaken: useCallback(
                    async (
                        name: string,
                        partner: { serviceProviderId?: string; portfolioCompanyId?: string },
                    ) => {
                        const wanted = name.trim().toLowerCase();
                        if (!wanted) return false;
                        // `name` is a contains-filter and the list includes inactive deals,
                        // which also hold their name; compare exactly, ignoring case, as the
                        // unique index does.
                        const page = await api.deals.list({
                            name: name.trim(),
                            service_provider_id: partner.serviceProviderId,
                            portfolio_company_id: partner.portfolioCompanyId,
                            limit: 100,
                        });
                        return page.items.some((d) => d.name.trim().toLowerCase() === wanted);
                    },
                    [],
                ),
                useCopySource: () => copySource,
                onLoadCopySource: loadCopySource,
                onDealCreateSuccess: useCallback(
                    (createdDeal?: TApiDeal, partnerId?: string) => {
                        // A copy lands on the new deal so the carried-over setup can be reviewed.
                        if (createdDeal && partnerId) {
                            navigate(
                                routerPaths.admin.businessPartnerDealDetail(
                                    partnerId,
                                    createdDeal.id,
                                ),
                            );
                            return;
                        }
                        navigate(routerPaths.admin.businessPartners);
                    },
                    [navigate],
                ),
                onExtractColumns: useCallback(async (file: File, headerRowNumber: number) => {
                    return api.deals.extractColumns(file, headerRowNumber);
                }, []),
                onValidatePropcodeMappingFile: useCallback(async (file: File) => {
                    return api.deals.validatePropcodeMappingFile(file);
                }, []),
                // LaSalle reads the Yardi Trial Balance as it is: the endpoint takes the file,
                // start period and deal file type, and ignores the (empty) column mappings.
                onValidateOTB: useCallback(
                    async (file: File, startPeriod: string, dealFileType: TApiDealFileType) => {
                        return api.deals.validateOTB(
                            file,
                            [],
                            startPeriod,
                            undefined,
                            dealFileType,
                        );
                    },
                    [],
                ),
                onUploadOTB: useCallback(async (dealId: string, file: File) => {
                    return api.deals.uploadOTB(dealId, file);
                }, []),
                onCancel: useCallback(
                    (_bpId?: string) => {
                        navigate(routerPaths.admin.businessPartners);
                    },
                    [navigate],
                ),
            };
        },
    });

    return { ViewDealCreate };
}
